import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { coreAPI } from '../../lib/api';
import type {
  UserProfile,
  PrimaryContactCompanyOption,
  PrimaryContactFor,
} from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import {
  Star, Plus, X, Loader2, Search, Building2, Truck, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

interface PrimaryContactSectionProps {
  /** The user whose designations are being edited. */
  selectedUser: UserProfile;
  /** False for lower-tier targets — the section renders read-only. */
  canManage: boolean;
  /** Called after a successful save so the parent list can refresh. */
  onSaved?: () => void;
}

type ContactType = 'supplier' | 'customer';

/** Order-independent key for a set of company ids, for dirty-checking. */
const toKey = (ids: number[]) => [...ids].sort((a, b) => a - b).join(',');

/**
 * "Primary Contact For" editor in the User Management user modal.
 *
 * A user can front any number of suppliers and customers, but each company has
 * exactly one primary contact — so picking a company that already has one takes
 * the designation over. The picker surfaces the incumbent before the admin
 * saves, and the save response confirms who was displaced.
 *
 * Writes are absolute: the whole company set is sent on save, and the backend
 * reconciles. Read-only elsewhere (the Supplier Detail → Users tab renders the
 * resulting badge from UserProfile.primary_contact_for).
 */
export function PrimaryContactSection({
  selectedUser,
  canManage,
  onSaved,
}: PrimaryContactSectionProps) {
  const [selected, setSelected] = useState<PrimaryContactFor[]>([]);
  // Comma-joined company ids as last persisted. Kept locally rather than read
  // off the prop because the parent's list copy isn't refreshed in place after
  // a save — without this the Save button would stay up on a saved section.
  const [baseline, setBaseline] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<PrimaryContactCompanyOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [saving, setSaving] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Seed from the profile payload, and re-seed when the modal is reused for a
  // different user (the component stays mounted across selections).
  useEffect(() => {
    const assignments = selectedUser.primary_contact_for ?? [];
    setSelected(assignments);
    setBaseline(toKey(assignments.map((a) => a.company)));
    setPickerOpen(false);
    setSearch('');
  }, [selectedUser.id, selectedUser.primary_contact_for]);

  // A user only fronts companies on their own side of the platform: a supplier
  // user can be primary contact for suppliers only, a customer user for
  // customers only. Derived, never chosen — the backend rejects a cross-side
  // assignment, so offering the other list would only produce a 400.
  const contactType: ContactType =
    selectedUser.company_type === 'supplier' ? 'supplier' : 'customer';

  const selectedIds = useMemo(() => toKey(selected.map((a) => a.company)), [selected]);
  const isDirty = baseline !== selectedIds;

  // Debounced server-side search — a tenant can have thousands of suppliers, so
  // the list is never fetched whole.
  useEffect(() => {
    if (!pickerOpen) return;
    let cancelled = false;
    setOptionsLoading(true);
    const timer = setTimeout(() => {
      coreAPI
        .getPrimaryContactCompanies({
          user_profile: selectedUser.id,
          search: search.trim(),
        })
        .then((res) => {
          if (cancelled) return;
          setOptions(res.data.results);
          setTruncated(res.data.truncated);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          console.error('Failed to load companies:', err);
          setOptions([]);
        })
        .finally(() => {
          if (!cancelled) setOptionsLoading(false);
        });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [pickerOpen, selectedUser.id, search]);

  // Close the picker on outside click, matching SearchableSelect's behaviour.
  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pickerOpen]);

  const addCompany = useCallback((option: PrimaryContactCompanyOption) => {
    setSelected((prev) => {
      if (prev.some((a) => a.company === option.id)) return prev;
      return [
        ...prev,
        {
          // Negative placeholder id — the row doesn't exist server-side until
          // save, and the real id comes back in the response.
          id: -option.id,
          company: option.id,
          company_name: option.name,
          contact_type: option.company_type,
        },
      ];
    });
    setSearch('');
  }, []);

  const removeCompany = useCallback((companyId: number) => {
    setSelected((prev) => prev.filter((a) => a.company !== companyId));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await coreAPI.setPrimaryContacts(
        selectedUser.id,
        selected.map((a) => a.company),
      );
      // Adopt the server's view as the new baseline — the placeholder ids the
      // picker assigned are replaced by the real rows, and Save disappears.
      const saved = res.data.assignments.map((a) => ({
        id: a.id,
        company: a.company,
        company_name: a.company_name,
        contact_type: a.contact_type,
      }));
      setSelected(saved);
      setBaseline(toKey(saved.map((a) => a.company)));

      const replaced = res.data.replaced;
      if (replaced.length > 0) {
        toast.success(
          `Primary contact updated — replaced ${replaced
            .map((r) => `${r.previous_contact} on ${r.company_name}`)
            .join(', ')}`,
        );
      } else {
        toast.success('Primary contact assignments saved');
      }
      onSaved?.();
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { data?: { message?: string; company_ids?: string[]; detail?: string } };
      };
      const data = axiosErr.response?.data;
      toast.error(
        data?.company_ids?.[0] || data?.message || data?.detail
        || 'Failed to save primary contact assignments',
      );
    } finally {
      setSaving(false);
    }
  };

  const isSupplierSide = contactType === 'supplier';
  const sideLabel = isSupplierSide ? 'Suppliers' : 'Customers';
  const SideIcon = isSupplierSide ? Truck : Building2;

  const renderChips = (assignments: PrimaryContactFor[], label: string, Icon: typeof Truck) => (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label} ({assignments.length})
      </div>
      {assignments.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">None</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {assignments.map((assignment) => (
            <Badge
              key={assignment.company}
              variant="secondary"
              className="gap-1.5 py-1 pl-2 pr-1.5"
            >
              {assignment.company_name}
              {canManage && (
                <button
                  type="button"
                  onClick={() => removeCompany(assignment.company)}
                  className="rounded-sm hover:bg-muted-foreground/20 p-0.5"
                  aria-label={`Remove ${assignment.company_name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium flex items-center gap-2">
          <Star className="h-4 w-4 text-amber-500" />
          Primary Contact For
        </Label>
        {canManage && isDirty && (
          // type="button" is load-bearing: this section also renders inside the
          // supplier-detail Edit User form, where a bare <button> would default
          // to submit and fire the profile save instead.
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="gap-2"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Save
          </Button>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        {sideLabel} this user is the named primary contact for. Only the companies
        they can access (their own company plus their Current Company Access) can be
        picked. Each company has one primary contact — assigning here replaces
        whoever holds it now.
      </p>

      {renderChips(selected, sideLabel, SideIcon)}

      {canManage && (
        // Laid out in the flow rather than as an absolute dropdown: this
        // section lives inside the modal's overflow-y-auto tab panel, which
        // clips any absolutely-positioned popover.
        <div ref={pickerRef} className="space-y-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => setPickerOpen((open) => !open)}
          >
            <Plus className="h-3.5 w-3.5" />
            Add company
          </Button>

          {pickerOpen && (
            <div className="w-full max-w-md bg-background border border-border rounded-md shadow-sm">
              {/* No side switcher: the list is fixed to the user's own side,
                  and the server enforces the same rule on save. */}
              <div className="flex items-center border-b border-border px-3 py-2">
                <Search className="h-4 w-4 text-muted-foreground mr-2 shrink-0" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  // Same reason as the Save button: inside the Edit User form,
                  // Enter here would submit the profile rather than do nothing.
                  onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                  placeholder={`Search ${sideLabel.toLowerCase()}...`}
                  className="h-7 border-0 px-0 shadow-none focus-visible:ring-0"
                />
              </div>

              <div className="max-h-[240px] overflow-y-auto">
                {optionsLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : options.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-muted-foreground">
                    {search
                      ? 'No companies match your search'
                      : 'This user has no company access yet — grant it on the '
                        + 'Related Companies tab first.'}
                  </div>
                ) : (
                  options.map((option) => {
                    const alreadySelected = selected.some((a) => a.company === option.id);
                    const heldByOther =
                      option.current_contact
                      && option.current_contact.user_profile !== selectedUser.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        disabled={alreadySelected}
                        onClick={() => addCompany(option)}
                        className={`w-full text-left px-3 py-2 border-b border-border/50 last:border-0 ${
                          alreadySelected ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted'
                        }`}
                      >
                        <div className="text-sm font-medium flex items-center gap-2">
                          {option.name}
                          {option.supplier_code && (
                            <span className="text-xs text-muted-foreground">
                              {option.supplier_code}
                            </span>
                          )}
                        </div>
                        {alreadySelected ? (
                          <div className="text-xs text-muted-foreground">Already selected</div>
                        ) : heldByOther ? (
                          <div className="text-xs text-amber-600 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Replaces {option.current_contact?.name}
                          </div>
                        ) : null}
                      </button>
                    );
                  })
                )}
                {truncated && !optionsLoading && (
                  <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border">
                    Showing the first 50 matches — refine your search to narrow.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PrimaryContactSection;
